import { useState } from 'react';
import { cn } from '../../../common/functions/cn';
import RequiredIndicator from '../../common/required-indicator/required-indicator.component';
import ErrorLabel from '../../labels/error-label';
import FieldLabel from '../../labels/field-label';
import { iconMap } from '../../navigation/icon-mapper';
import OdinIcon, { OdinIconSize } from '../../odin-icon/odin-icon.component';

import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
} from '../combobox/combobox';
import type { IconItem, IconSelectorFieldProps } from './icon-selector-field.props';

const allIcons = Object.keys(iconMap)
  .map((key) => ({
    icon: key,
    iconFileName: iconMap[key],
  }))
  .sort((a, b) => a.iconFileName.localeCompare(b.iconFileName));

export function IconSelectorField({
  name,
  label,
  value,
  readOnly,
  disabled,
  required,
  error,
  onChange,
  className,
  placeholder = 'Search icons...',
}: IconSelectorFieldProps) {
  const [selectedValue, setSelectedValue] = useState<IconItem | null>(
    value ? { icon: value, iconFileName: iconMap[value] } : null
  );

  const handleValueChange = (nextValue: IconItem | null) => {
    setSelectedValue(nextValue);

    if (nextValue) {
      onChange?.(nextValue.icon);
    } else {
      onChange?.(undefined);
    }
  };

  if (readOnly) {
    return (
      <div className={className}>
        {label && (
          <div className="flex">
            <FieldLabel label={label} name={name} />
            <RequiredIndicator readOnly={readOnly} required={required} />
          </div>
        )}
        {selectedValue && (
          <div className="flex items-center gap-2 border-none bg-transparent">
            <OdinIcon icon={selectedValue.iconFileName} size={OdinIconSize.ExtraSmall} />
            <span className="text-sm">{selectedValue.iconFileName}</span>
          </div>
        )}
        {error && <ErrorLabel>{error}</ErrorLabel>}
      </div>
    );
  }

  return (
    <>
      {label && (
        <div className="flex">
          <FieldLabel label={label} name={name} />
          <RequiredIndicator readOnly={readOnly} required={required} />
        </div>
      )}
      <ComboboxRoot
        items={allIcons}
        itemToStringLabel={(icon) => icon.iconFileName}
        value={selectedValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        isItemEqualToValue={isItemEqualToValue}
      >
        <ComboboxInput
          placeholder={placeholder}
          disabled={disabled}
          aria-invalid={error !== undefined}
          className={cn({ '[&_input]:pr-14 [&_input]:pl-8': selectedValue, '[&_input]:pr-8': !selectedValue })}
          showClear
        >
          {selectedValue && (
            <div className="absolute left-0 mx-1 flex w-5 items-center justify-center">
              <OdinIcon icon={selectedValue.iconFileName} size={OdinIconSize.ExtraSmall} />
            </div>
          )}
        </ComboboxInput>
        <ComboboxContent>
          <ComboboxEmpty>No icons found</ComboboxEmpty>
          <ComboboxList>
            {(icon: IconItem) => (
              <ComboboxItem key={icon.icon} value={icon} className="flex items-center gap-3 px-3 py-2 leading-4">
                <OdinIcon icon={icon.iconFileName} size={OdinIconSize.Small} />
                <span className="flex flex-col text-sm">{icon.iconFileName}</span>
              </ComboboxItem>
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
      {error && <ErrorLabel>{error}</ErrorLabel>}
    </>
  );
}

const isItemEqualToValue = (item: IconItem, value: IconItem | null) => {
  return item.icon === value?.icon && item.iconFileName === value?.iconFileName;
};
